import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { formatCurrency } from '../utils/helpers';
import { PieChart as PieIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ordered so colours stay consistent regardless of ordering
const COLOR_MAP = {
  'Insurance':   '#1e40af', // blue-800
  'Mutual Fund': '#059669', // emerald-600
  'Loan':        '#ea580c', // orange-600
  'Deposit':     '#0d9488', // teal-600
  'PPF':         '#7c3aed', // violet-600
  'Post Office': '#64748b', // slate-500
  'Other':       '#94a3b8', // slate-400
};

const shortMoney = (n) => {
  const num = Number(n) || 0;
  if (num >= 1e7) return `₹${(num / 1e7).toFixed(1)}Cr`;
  if (num >= 1e5) return `₹${(num / 1e5).toFixed(1)}L`;
  if (num >= 1e3) return `₹${(num / 1e3).toFixed(0)}K`;
  return `₹${num}`;
};

/**
 * A compact per-client exposure chart. Renders a small donut with a legend
 * showing each bucket's share of the client's total financial commitment.
 * Silent (renders nothing) if the client has no exposure yet.
 */
const ClientExposureChart = ({ clientId, compact = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/clients/${clientId}/exposure`, { withCredentials: true })
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  const chartData = useMemo(() => {
    if (!data?.breakdown) return [];
    return data.breakdown.map((b) => ({ name: b.label, value: b.amount, count: b.count }));
  }, [data]);

  if (loading) return <div className="text-[10px] text-slate-400" data-testid={`exposure-loading-${clientId}`}>Loading exposure…</div>;
  if (!data || data.total === 0 || chartData.length === 0) {
    return (
      <div className="text-[10px] text-slate-400 flex items-center gap-1" data-testid={`exposure-empty-${clientId}`}>
        <PieIcon className="h-3 w-3" /> No exposure yet
      </div>
    );
  }

  const size = compact ? 64 : 96;
  const inner = compact ? 18 : 30;
  const outer = compact ? 30 : 44;

  return (
    <div className="flex items-center gap-3" data-testid={`exposure-chart-${clientId}`}>
      <div style={{ width: size, height: size, minWidth: size, minHeight: size, flexShrink: 0 }}>
        <ResponsiveContainer width={size} height={size}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={inner}
              outerRadius={outer}
              paddingAngle={1}
              stroke="none"
            >
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={COLOR_MAP[entry.name] || COLOR_MAP.Other} />
              ))}
            </Pie>
            <Tooltip
              formatter={(v, n) => [formatCurrency(v), n]}
              contentStyle={{ fontSize: 10, padding: '4px 6px', borderRadius: 6 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-semibold text-slate-900 leading-tight">{shortMoney(data.total)} total</p>
        <ul className="mt-0.5 space-y-0.5">
          {chartData.slice(0, 3).map((b) => (
            <li key={b.name} className="flex items-center gap-1.5 text-[10px] text-slate-600 truncate">
              <span className="inline-block h-2 w-2 rounded-sm shrink-0" style={{ backgroundColor: COLOR_MAP[b.name] || COLOR_MAP.Other }} />
              <span className="truncate">{b.name}</span>
              <span className="ml-auto text-slate-400 shrink-0">{shortMoney(b.value)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ClientExposureChart;
